import { useState, useEffect, useRef } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Check if already installed (standalone mode)
    // Guard for SSR and test environments where matchMedia may not exist
    const isStandalone =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) {
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      // Show our custom install prompt
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;

    // Show the native install prompt
    await deferredPrompt.current.prompt();

    // Wait for the user's choice
    await deferredPrompt.current.userChoice;

    // Hide our prompt regardless of outcome
    setShowPrompt(false);
    deferredPrompt.current = null;
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    deferredPrompt.current = null;
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-labelledby="install-prompt-title"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:w-80 bg-neutral-800 rounded-lg shadow-2xl border border-neutral-700 p-4 z-50"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h3 id="install-prompt-title" className="font-medium text-white">
            Install NFA
          </h3>
          <p className="text-sm text-neutral-400 mt-1">
            Access your kits offline and get a faster experience
          </p>
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button
          onClick={handleDismiss}
          className="flex-1 px-4 py-2 text-sm text-neutral-400 hover:text-white transition-colors"
          aria-label="Not now"
        >
          Not now
        </button>
        <button
          onClick={handleInstall}
          className="flex-1 px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium"
          aria-label="Install"
        >
          Install
        </button>
      </div>
    </div>
  );
}
